import { z } from 'zod';

/**
 * A calendar date, `YYYY-MM-DD`. SPEC §7: calendar dates are strings everywhere —
 * DB, API and engine. Only true instants are `Date`.
 */
export const dateStr = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected a YYYY-MM-DD calendar date');

// The `DateStr` type itself lives in `../dates.ts`, next to the arithmetic that uses it.

/** A Mongo ObjectId in its 24-hex string form, as it crosses the API. */
export const objectIdStr = z.string().regex(/^[0-9a-fA-F]{24}$/, 'expected an id');

/** An instant, accepted as an ISO string or a Date and normalised to a Date. */
export const instant = z.union([z.string().datetime(), z.date()]).transform((v) => new Date(v));

export const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected a #rrggbb colour');

export const coords = z.object({
  latitude: z.number(),
  longitude: z.number(),
  accuracy: z.number().optional(),
});
export type Coords = z.infer<typeof coords>;

/** Renderable at upload time — a jpg/png is always exactly one page; a PDF renders
 * only its first. Anything else has zero. Shared by list-item and job attachments. */
export const RENDERABLE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'] as const;

/**
 * What an upload may declare itself to be — the documents and photos a shop actually
 * attaches, and nothing else. A whitelist because the server streams a file back under
 * the content type it arrived with: `text/html` or `image/svg+xml` stored here would be
 * a page of someone else's script served from this app's own origin. Mirrors the
 * extension table in `server/src/services/storage.ts`.
 */
export const UPLOAD_CONTENT_TYPES = [
  ...RENDERABLE_CONTENT_TYPES,
  'image/heic',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
] as const;

/** One uploaded file — stored in the bucket, keyed by `_id`. Used both by a list
 * item's own attachments and by a job's reference attachments. */
export const attachment = z.object({
  _id: objectIdStr,
  filename: z.string(),
  contentType: z.string(),
  size: z.number(),
  /** How many `pages/{NNN}.png` + `pages/{NNN}_thumb.png` renditions exist in the
   * bucket. 0 means none — either the type isn't renderable, or generation failed. */
  pages: z.number().default(0),
  uploadedAt: z.coerce.date(),
  uploadedBy: z.string().optional(),
});
export type Attachment = z.infer<typeof attachment>;
